import { track } from "@amplitude/analytics-browser";
import { Avatar, Box } from "@material-ui/core";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import { useMutation } from "@tanstack/react-query";
import { clsx } from "clsx";
import { useDispatch, useSelector } from "react-redux";
import { postFocus } from "../../../learning-path/learningPath.service";
import {
  AmplitudeEventType,
  type AmplitudeTrack,
} from "../../../models/amplitude";
import { type RootState } from "../../../store";
import { useApiAllFocus, useApiParticipantFocus } from "../../hooks";
import useModal from "../../hooks/useModal";
import { EditGoalIcon, FallbackProfileIcon, GoalIcon } from "../../icons";
import { setClose } from "../../request/actions/requestActions";
import { setActiveLearningPath } from "../../slices/activeLearningPath";
import { DialogUpdateGoal } from "../dialog-update-goal/dialog-update-goal.component";
import { GoalStyles } from "./goal.styles";

type Selector = {
  participantProfileId: RootState["profile"]["participantProfileId"];
  profile: RootState["profile"];
  activeLearningPath: RootState["activeLearningPath"];
};

type Props = {
  editIconClass?: string;
  goalIconClass?: string;
  columnGap?: number;
  variant?: "default" | "avatar" | "title";
  id?: string;
  fullWidth?: boolean;
};

export const Goal = ({
  goalIconClass,
  editIconClass,
  columnGap = 20,
  id = "goal",
  variant = "default",
  fullWidth,
}: Props) => {
  const styles = GoalStyles();
  const dispatch = useDispatch();
  const { isShowing, toggle } = useModal();
  const { useQuery: getParticipantFocus } = useApiParticipantFocus();
  const { useQuery: getAllFocus } = useApiAllFocus();

  const isTitleVariant = variant == "title";
  const isAvatarVariant = variant == "avatar";

  const { participantProfileId, profile, activeLearningPath } = useSelector<
    RootState,
    Selector
  >((state) => ({
    participantProfileId: state.profile.participantProfileId,
    profile: state.profile,
    activeLearningPath: state.activeLearningPath,
  }));

  const { data: allFocus } = getAllFocus();
  const {
    data: participantFocus,
    error,
    isError,
  } = getParticipantFocus({
    profileId: participantProfileId,
    focusId: activeLearningPath,
  });

  const { mutate } = useMutation(postFocus, {
    onSuccess: (_, variables) => {
      const newFocusSelected = allFocus.find(
        ({ focusId }) => focusId == variables.focusId
      );
      const amplitudeEvent: AmplitudeTrack<AmplitudeEventType.LEARNING_PATH_SELECTED> =
        {
          event_type: AmplitudeEventType.LEARNING_PATH_SELECTED,
          user_id: participantProfileId,
          event_properties: {
            page_url: window.location.href,
            selected_experience: newFocusSelected.name,
          },
        };
      track(amplitudeEvent);
      dispatch(setActiveLearningPath(variables.focusId));
    },
  });

  const handleUpdateGoal = (newFocusId: number) => {
    mutate({
      participantProfileId,
      focusId: newFocusId,
    });
    toggle();
  };

  const handleEditGoalClick = () => {
    toggle();
  };

  const handleCloseModal = () => {
    toggle();
  };

  const handleClose = () => dispatch(setClose());

  const avatar = () => {
    return profile?.profilePicturePath ? (
      <Avatar
        className={styles.avatar}
        alt={profile?.name}
        src={profile?.profilePicturePath}
      />
    ) : (
      <Avatar className={styles.avatar}>
        <FallbackProfileIcon />
      </Avatar>
    );
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="start"
        alignItems="center"
        alignContent="start"
        className={clsx(styles.root, { [styles.fullWidth]: fullWidth })}
        gridColumnGap={columnGap}
        data-testid="goalContainer"
        id={id}
      >
        <Box>
          {isAvatarVariant ? avatar() : <GoalIcon className={goalIconClass} />}
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          alignContent="center"
          className={clsx(styles.goal, {
            [styles.fullWidth]: fullWidth,
            [styles.goalBox]: !isTitleVariant,
          })}
          gridColumnGap={30}
        >
          <Box
            className={clsx({
              [styles.avatarText]: isAvatarVariant,
              [styles.text]: !isTitleVariant,
              [styles.title]: isTitleVariant,
            })}
          >
            {participantFocus?.name}
          </Box>
          <Box className={styles.edit}>
            {participantFocus && (
              <EditGoalIcon
                onClick={handleEditGoalClick}
                className={editIconClass}
              />
            )}
          </Box>
        </Box>
      </Box>
      <DialogUpdateGoal
        isOpen={isShowing}
        onClose={handleCloseModal}
        onConfirm={handleUpdateGoal}
        currentFocus={participantFocus?.focusId}
      />
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={isError}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};
